"use client";

import useSWR, { mutate as globalMutate } from "swr";
import { useAuth } from "@clerk/nextjs";
import { createFetcher } from "@/lib/api";
import type { DriversResponse, Driver } from "@/lib/api-types";
import { useCallback } from "react";

export function useDrivers(page = 1, limit = 25) {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<DriversResponse>(
    `/api/v1/drivers?page=${page}&limit=${limit}`,
    createFetcher(getToken),
  );
  return {
    drivers: data?.drivers ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? page,
    isLoading,
    error,
  };
}

export function useDriver(id: string | undefined) {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<Driver>(
    id ? `/api/v1/drivers/${id}` : null,
    createFetcher(getToken),
  );
  return { driver: data, isLoading, error };
}

export function useDriverMutations() {
  const { getToken } = useAuth();

  const create = useCallback(
    async (body: {
      name: string;
      phone: string;
      email?: string;
      license_num?: string;
      carrier_type?: string;
    }) => {
      const token = await getToken();
      const res = await fetch("/api/v1/drivers", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: string) =>
        typeof key === "string" && key.startsWith("/api/v1/drivers"),
      );
      return res.json();
    },
    [getToken],
  );

  const update = useCallback(
    async (id: string, body: Partial<Driver>) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/drivers/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: string) =>
        typeof key === "string" && key.startsWith("/api/v1/drivers"),
      );
      return res.json();
    },
    [getToken],
  );

  const remove = useCallback(
    async (id: string) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/drivers/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to delete driver");
      await globalMutate((key: string) =>
        typeof key === "string" && key.startsWith("/api/v1/drivers"),
      );
    },
    [getToken],
  );

  return { create, update, remove };
}
